/* eslint-disable no-console */
import axios from "axios";
import Cookies from "universal-cookie";
import * as Sentry from '@sentry/react'
import { SignJWT } from 'jose';
import { getAPIURL } from "../Helpers/apiUrl";
import { DEFAULT_TIMEZONE } from "../Helpers/Constants";
import { buildSentryExtra } from "../utils/sanitizeData";
import { safeError } from "../utils/logger";

const cookies = new Cookies();
// Read per request: a tab that cached it kept a live token after another tab logged out and cleared selectedStore.
const accessToken = () => cookies.get("accessToken");
const API_URL = process.env.REACT_APP_API_URL;
let key = process.env.REACT_APP_JWT_KEY;
let payload = {
  iss: process.env.REACT_APP_JWT_ISS,
};

// Secure JWT token generation function using jose library
async function generateToken() {
  try {
    const secret = new TextEncoder().encode(key);
    
    const token = await new SignJWT(payload)
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime('60m')
      .sign(secret);
    
    return token;
  } catch (error) {
    safeError("Error generating JWT token:", error);
    // Fallback to a simple string if JWT generation fails
    return `${payload.iss}_${Date.now()}`;
  }
}

export const errorResponse = (error) =>
  error?.response ?? { status: 0, data: { message: "We couldn't reach the server. Try again." } };

// No response (network drop, page reload mid-request): give callers a response to read and skip Sentry.
function handleApiError(error, url, body) {
  safeError(`${url} error`, error);
  if (!error?.response) return errorResponse(error);
  const sentryExtra = buildSentryExtra({
    error: error.response.data,
    body,
    url,
    message: error.response.data?.message || "",
    generateTime: new Date().toLocaleString(),
  });
  Sentry.captureMessage(
    `URL:${url}, message:${sentryExtra.message}, GENERATE_TIME:${sentryExtra.generateTime}`,
    { extra: sentryExtra, level: 'warning' }
  );
  return error.response;
}

export async function callPostApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  let storetimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  if (body?.orderTimezone) {
    storetimezone = body?.orderTimezone;
  }
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "POST",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": storetimezone, requestedstoreid },
    });
    return result;
  } catch (error) {
    return handleApiError(error, url, body);
  }
}

export async function callPostApiv2(url, body, headers, notoken) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
    delete body?.selectedStore;
  }else if (body?.selectedStore){
    selectedStore = body?.selectedStore;
    delete body?.selectedStore;
  } else if (body?.zipcode){
    headers["zipcode"] = body?.zipcode;
    delete body?.zipcode;
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  if (notoken) {
    authHeader.authorization = "";
  }
  let storetimezone = localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE;
  if (body?.orderTimezone) {
    storetimezone = body?.orderTimezone;
  }
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "POST",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": storetimezone, requestedstoreid },
    });
    return result;
  } catch (error) {
    handleApiError(error, url, body);
    throw error;
  }
}

export async function callGetApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "GET",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    return handleApiError(error, url, body);
  }
}

export async function callGetApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "GET",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    handleApiError(error, url, body);
    throw error;
  }
}

export async function callPutApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "PUT",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    return handleApiError(error, url, body);
  }
}

export async function callPutApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "PUT",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    handleApiError(error, url, body);
    throw error;
  }
}

export async function callDeleteApi({ url, body, headers }) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "DELETE",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    return handleApiError(error, url, body);
  }
}

export async function callDeleteApiv2(url, body, headers) {
  let selectedStore = localStorage.getItem("selectedStore");
  let requestedstoreid = selectedStore;
  if(typeof(localStorage.getItem("pagewiseallstorecheckbox"))!='undefined' && localStorage.getItem("pagewiseallstorecheckbox")!=null && localStorage.getItem("pagewiseallstorecheckbox")==0){
    selectedStore = parseInt(0);
  }
  let acc = await generateToken(); // Await the async function
  let authHeader = accessToken() ? { authorization: accessToken() } : {};
  authHeader["x-token"] = acc;
  try {
    const result = await axios({
      url: getAPIURL(url),
      method: "DELETE",
      data: body,
      timeout: 120000,
      headers: { ...authHeader, ...headers, selectedstoreid:selectedStore, "requestfrom":"admin", "storetimezone": localStorage.getItem("storeTimezone") || DEFAULT_TIMEZONE, requestedstoreid },
    });
    return result;
  } catch (error) {
    return handleApiError(error, url, body);
  }
}
